import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, Button, List, ListItem, ListItemButton, ListItemText, CircularProgress, Checkbox, FormControlLabel } from '@mui/material';
import { IInstitution } from '../../interfaces/institution.interface';
import {InstitutionService} from "../../services/institution.service";
import { AuthService } from '../../services/auth.service';

/** Platform staff may list every institution; everyone else only sees the ones they belong to. */
const isPlatformUser = (): boolean => {
  const roles = AuthService.getCurrentUser()?.roles as unknown;
  const list = Array.isArray(roles) ? roles : String(roles ?? '').split(',');
  return list.map((r: string) => r.trim()).some((r: string) => r === 'SystemAdmin' || r === 'SaaSOperator');
};

const toList = (data: unknown): IInstitution[] => {
  if (Array.isArray(data)) return data as IInstitution[];
  const content = (data as { content?: unknown } | null)?.content;
  return Array.isArray(content) ? (content as IInstitution[]) : [];
};

interface InstitutionSelectionModalProps {
  open: boolean;
  institutionIds: string[];
  /** `remember` is the "Remember my choice" checkbox at the moment of the pick. */
  onSelect: (institutionId: string, remember: boolean) => void | Promise<{ success: boolean; error?: string } | void>;
  /** When set, the modal is a switcher: it can be dismissed and marks the current institution. */
  onClose?: () => void;
  currentInstitutionId?: string | null;
  /** The institution this user asked to open automatically at login, if any (ticks the checkbox). */
  rememberedInstitutionId?: string | null;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

const InstitutionSelectionModal: React.FC<InstitutionSelectionModalProps> = ({
  open,
  institutionIds,
  onSelect,
  onClose,
  currentInstitutionId,
  rememberedInstitutionId
}) => {
  const [remember, setRemember] = useState<boolean>(Boolean(rememberedInstitutionId));
  const [attempt, setAttempt] = useState<number>(0);
  const [institutions, setInstitutions] = useState<IInstitution[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchInstitutions = async () => {
      try {
        setLoading(true);
        const data = isPlatformUser()
          ? await InstitutionService.getInstitutions(0, 200)
          : await InstitutionService.getLoggedInUserInstitutions(0, 200);
        const allInstitutions = toList(data);

        // Filter institutions by the IDs the user has access to
        const userInstitutions = allInstitutions.filter(
          (institution: IInstitution) => institutionIds.includes(institution.id)
        );

        setInstitutions(userInstitutions);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch institutions', err);
        setError('Failed to load institutions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (open && institutionIds.length > 0) {
      fetchInstitutions();
    }
  }, [open, institutionIds, attempt]);

  const [selectError, setSelectError] = useState<string | null>(null);

  const handleInstitutionSelect = async (institutionId: string) => {
    setSelectError(null);
    const result = await onSelect(institutionId, remember);
    if (result && result.success === false) {
      setSelectError(result.error ?? 'Could not select this institution.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="institution-selection-modal"
      aria-describedby="select-your-institution"
    >
      <Box sx={modalStyle} data-help-page="institutions.select.page">
        <Typography id="institution-selection-modal" variant="h6" component="h2" mb={2}>
          Select Institution
        </Typography>
        <Typography id="select-your-institution" mb={2}>
          You have access to multiple institutions. Please select one to continue.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" my={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box>
            <Typography color="error">{error}</Typography>
            <Button
              variant="contained"
              sx={{ mt: 2 }}
              onClick={() => setAttempt((n) => n + 1)}
              data-help="institutions.select.retry"
            >
              Retry
            </Button>
          </Box>
        ) : (
          <>
          {selectError && <Typography color="error" role="alert" mb={1}>{selectError}</Typography>}
          <List>
            {institutions.map((institution) => (
              <ListItem disablePadding key={institution.id}>
                <ListItemButton selected={institution.id === currentInstitutionId} onClick={() => handleInstitutionSelect(institution.id)} data-help="institutions.select.choose">
                  <ListItemText primary={institution.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
          <FormControlLabel
            data-help="institutions.select.remember"
            control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
            label="Remember my choice on this device"
          />
          <Typography variant="caption" color="text.secondary" display="block">
            You will go straight to this institution at login. Switch any time from the header.
          </Typography>
          </>
        )}
        {onClose && (
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button onClick={onClose} data-help="institutions.select.cancel">Cancel</Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default InstitutionSelectionModal;
